import { useAtom } from '@reatom/npm-react'
import styled, { css } from 'styled-components'

import { Tooltip } from '@blade/ui-kit'
import { CurrencyIcon } from '@root/entities/currency/ui'
import { AmountFormatted } from '@root/shared/core/format'
import { Separator as Separator_, WalletIcon } from '@root/shared/ui-kit'

import { useWalletAssetBalance } from '../../hooks'

import type { WalletModel } from '../../atoms'
import type { CurrencyModel } from '@root/entities/currency'
import type { ClassNameProps } from '@root/shared/react'

type WalletBalanceBadgeProps = {
  currency: CurrencyModel
  wallet: WalletModel
  onClick?: () => void
} & ClassNameProps

export const WalletBalanceBadge: React.FC<WalletBalanceBadgeProps> = ({
  className,
  wallet,
  currency,
  onClick,
}) => {
  const assetBalance = useWalletAssetBalance({
    wallet,
    currency,
  })
  const [color] = useAtom(wallet.color)
  const [name] = useAtom(wallet.name)

  return (
    <Tooltip content={name}>
      <Root $clickable={!!onClick} className={className} onClick={onClick}>
        <WalletAddress>
          <WalletIcon color={color} size={14} />
          ...{wallet.address.slice(-4)}
        </WalletAddress>
        <Separator vertical />
        <Balance>
          <AmountFormatted amount={assetBalance} roundingMode="trunc" />
          <CurrencyIcon withGoogleLens={false} currency={currency} size={14} />
        </Balance>
      </Root>
    </Tooltip>
  )
}

const Root = styled.div<{ $clickable: boolean }>`
  display: grid;
  grid-template-columns: auto 1px auto;
  align-items: center;

  padding-block: 2px;
  padding-inline: 4px;
  gap: 6px;
  height: 20px;

  border-radius: 12px;
  background: var(--background-3);

  ${({ $clickable }) =>
    $clickable &&
    css`
      cursor: pointer;
      transition: background 0.2s ease-in-out;
      &:hover {
        background: var(--background-4);
      }
    `}
`

const WalletAddress = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 3px;

  color: var(--text-9);
  text-align: center;
  font-size: 10px;
  font-weight: 500;
  line-height: 12px;
`

const Balance = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 4px;

  color: var(--text-11);

  font-size: 11px;
  font-weight: 700;
  line-height: 16px;

  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
`

const Separator = styled(Separator_)`
  background: #313030;
`
